/**
 * useEditorShortcuts: keyboard shortcuts for the floor plan editor.
 *
 * Same safety rule throughout: never hijack shortcuts while the user is
 * typing in a text input, since browsers already give native undo/copy/
 * paste there.
 *
 * Per the editor polish brief: keep this simple, do not over-engineer.
 * Each shortcut maps directly to an existing store action — no new
 * mutation logic lives here, this hook is purely "keyboard event ->
 * existing store action."
 */

import { useEffect } from 'react';
import { useEditorStore } from '../store/useEditorStore';

function isTypingInTextField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
}

export function useEditorShortcuts() {
  const undo = useEditorStore((state) => state.undo);
  const redo = useEditorStore((state) => state.redo);
  const deleteSelected = useEditorStore((state) => state.deleteSelected);
  const copySelected = useEditorStore((state) => state.copySelected);
  const pasteClipboard = useEditorStore((state) => state.pasteClipboard);
  const duplicateSelected = useEditorStore((state) => state.duplicateSelected);
  const selectAll = useEditorStore((state) => state.selectAll);
  const selectedIds = useEditorStore((state) => state.selectedIds);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (isTypingInTextField(e.target)) return;

      const isModifierPressed = e.metaKey || e.ctrlKey;

      // Delete/Backspace: remove selected object(s). No modifier needed —
      // matches Canva/Figma-style tools, and is safe here specifically
      // because we already guard against text-field focus above (so this
      // never fires while editing a label, for example).
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length > 0) {
        e.preventDefault();
        deleteSelected();
        return;
      }

      if (!isModifierPressed) return;

      if (e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        redo();
      } else if (e.key === 'z') {
        e.preventDefault();
        undo();
      } else if (e.key === 'y') {
        e.preventDefault();
        redo();
      } else if (e.key === 'a') {
        e.preventDefault();
        selectAll();
      } else if (e.key === 'c') {
        if (selectedIds.length === 0) return;
        e.preventDefault();
        copySelected();
      } else if (e.key === 'v') {
        e.preventDefault();
        pasteClipboard();
      } else if (e.key === 'd') {
        // Cmd/Ctrl+D for duplicate — common convention (browsers use it
        // for bookmarking, but inside the editor canvas this is more
        // useful, and we only intercept it while the editor is mounted).
        if (selectedIds.length === 0) return;
        e.preventDefault();
        duplicateSelected();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, deleteSelected, copySelected, pasteClipboard, duplicateSelected, selectAll, selectedIds]);
}
